import isNull from 'lodash/isNull'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { DocumentMapping } from '../../../models/documentMapping'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select } from '../../lib/toolBoxes/2D'
import { Overlay } from '../../lib/toolBoxes/2D/tools'
import {
    addOverlayChunkId,
    DEFAULT_OVERLAY_COLOR,
    removeOverlayChunkId,
    selectOpacityDict,
    selectOverlayColorDict,
    setOverlayChunkId,
    updateOverlayColor,
    updateOverlayOpacity,
} from '../../slices/2D'
import { NormalizedDocumentChunk, selectDocumentMappings, selectNormalizedDocumentChunks } from '../../slices/documents'
import { updateConflictResolutionFlag, updateOverlaidChunkLockedFlag } from '../../slices/masterSetPlan'
import { Vector2D } from '../../types'

function* insertOverlayForChunkId(overlayTool: Overlay, chunkId: number) {
    const normalizedChunks: NormalizedDocumentChunk[] = yield select(selectNormalizedDocumentChunks)
    const possibleChunk: NormalizedDocumentChunk | undefined = normalizedChunks.find(
        (docChunk) => docChunk.id === chunkId
    )

    if (!possibleChunk) return

    const { src } = possibleChunk

    const chunkOpacityDict: Record<number, number> = yield select(selectOpacityDict)
    const overlayColorDict: Record<number, string> = yield select(selectOverlayColorDict)
    const mappings: DocumentMapping[] | null = yield select(selectDocumentMappings)
    const persistedMapping = mappings?.find((map) => map.document_chunk_id === possibleChunk.id)
    const persistedOpacity = persistedMapping?.additional_data.overlayOpacity
    const persistedColor = persistedMapping?.additional_data.overlayColor

    const hasSessionOpacity = isFinite(chunkOpacityDict[possibleChunk.id])
    const hasPersistedOpacity = persistedOpacity !== undefined && isFinite(persistedOpacity)

    // Falls back to the last-saved value (lockOverlay.ts) until changed this session.
    const opacityPercent = hasSessionOpacity ? chunkOpacityDict[possibleChunk.id] : persistedOpacity
    const overlayColor: string = overlayColorDict[possibleChunk.id] ?? persistedColor ?? DEFAULT_OVERLAY_COLOR
    const chunkOpacity: number =
        opacityPercent !== undefined && isFinite(opacityPercent) ? opacityPercent / 100 : Overlay.DEFAULT_OPACITY

    // Seed the dicts too so toolbar controls reflect the persisted value, not just the raster.
    if (!hasSessionOpacity && hasPersistedOpacity) {
        yield put(updateOverlayOpacity({ id: possibleChunk.id, opacity: persistedOpacity as number }))
    }

    if (overlayColorDict[possibleChunk.id] === undefined && persistedColor !== undefined) {
        yield put(updateOverlayColor({ id: possibleChunk.id, color: persistedColor }))
    }

    const currentOverlayData: {
        scale: number
        rotation: number
        position: Vector2D
    } | null = yield call(overlayTool.extractOverlayDataToSave, possibleChunk.id)

    if (currentOverlayData) {
        yield call(overlayTool.removeOverlayById, possibleChunk.id)
    }

    yield call(overlayTool.insertOverlay, src, possibleChunk.id, chunkOpacity, overlayColor)
    yield call(overlayTool.activate)
    yield call(overlayTool.lockOverlay, false)

    if (currentOverlayData) {
        yield call(overlayTool.setOverlayPositionCoordinates, possibleChunk.id, currentOverlayData.position)

        yield call(overlayTool.updateOverlayScale, possibleChunk.id, currentOverlayData.scale ?? 1)

        yield call(overlayTool.rotateImageRaster, possibleChunk.id, currentOverlayData.rotation)
    }

    yield put(updateOverlaidChunkLockedFlag(false))
}

export function* handleInsertingNewImageOverlay(action: ReturnType<typeof setOverlayChunkId>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (isNull(manager)) return

    const [overlayTool]: [Overlay] = yield call(manager.getTools, [Overlay.NAME])

    if (action.payload === null) {
        yield call(overlayTool.resetOverlayRasters)
        yield call(overlayTool.resetSelectedImagesAndCrossHairs)
        yield call(manager.useTool, Select.NAME)
        yield put(updateConflictResolutionFlag(false))
        yield put(updateOverlaidChunkLockedFlag(false))

        return
    }

    yield* insertOverlayForChunkId(overlayTool, action.payload)
}

// Adds an overlay on top of whatever's already active -- the "+" button's action, unlike
// setOverlayChunkId which always starts from a clean slate.
export function* handleAddingImageOverlay(action: ReturnType<typeof addOverlayChunkId>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (isNull(manager)) return

    const [overlayTool]: [Overlay] = yield call(manager.getTools, [Overlay.NAME])

    const documentMappings: DocumentMapping[] | null = yield select(selectDocumentMappings)

    if (!documentMappings) return

    const mappingForChunkId = documentMappings.find((mapping) => mapping.document_chunk_id === action.payload)

    if (!mappingForChunkId) return

    // Called from returning to CR
    // That saga will handle the overlay insertion
    // handleInsertingNewOverlay
    if (mappingForChunkId.additional_data.estimatedBaseHomeId) return

    yield* insertOverlayForChunkId(overlayTool, action.payload)
}

// Removes one overlay from the active set (the "x" button) without disturbing the others.
export function* handleRemovingImageOverlay(action: ReturnType<typeof removeOverlayChunkId>) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (isNull(manager)) return

    const [overlayTool]: [Overlay] = yield call(manager.getTools, [Overlay.NAME])

    yield call(overlayTool.removeOverlayById, action.payload)
}

export function* watchForSelectingNewImageOverlay() {
    yield takeLatest(setOverlayChunkId.type, handleInsertingNewImageOverlay)
}

export function* watchForAddingImageOverlay() {
    yield takeLatest(addOverlayChunkId.type, handleAddingImageOverlay)
}

export function* watchForRemovingImageOverlay() {
    yield takeLatest(removeOverlayChunkId.type, handleRemovingImageOverlay)
}
